// client/src/world/flora/material.ts
import * as THREE from "three";
import { addStylizedShading } from "../stylizedShading";
import { WIND_GLSL, WIND_UNIFORMS } from "./wind";
import { TREE_WIND_GLSL } from './treeWind';

/** Shared wind clock — one uniform object referenced by every flora material. */
const WIND = { value: 0 };

export function setFloraTime(t: number): void {
  WIND.value = t;
}

/** Shared distance-fade scale — rides the DETAIL DISTANCE setting so ground
 *  cover and rocks dissolve in step with how far the terrain itself resolves.
 *  One uniform object referenced by every flora material: slider changes
 *  retune ALL live scatter instantly, no rebuild. */
const FADE_SCALE = { value: 1 };

export function setFloraFadeScale(k: number): void {
  FADE_SCALE.value = Math.min(2.5, Math.max(0.6, k));
}

/** Shared EYE — the player camera's position in scene (render) space. The
 *  distance fades measure from here, NOT from view-space depth: the shadow
 *  pass renders from the sun's ortho camera ~10 km sunward, so `-mvPosition.z`
 *  there is the LIGHT's distance, identical for every fragment. Faded on that,
 *  the depth twin inverts against the beauty pass — far-band trees stay fully
 *  opaque in the shadow map while invisible on screen (orphan shadows on bare
 *  ground), and nearby fadeFar props are discarded from the shadow map and
 *  cast nothing. One world-space distance to one eye keeps both passes honest. */
const EYE = { value: new THREE.Vector3() };

/** The shared per-frame uniform objects, for other flora shaders (the grass
 *  blade field) that must fade and tick on exactly the same clock. */
export const FLORA_SHARED = { WIND, FADE_SCALE, EYE };

export function setFloraEye(p: THREE.Vector3): void {
  EYE.value.copy(p);
}

/** Current flora clock (the same time base the wind/fade shader reads). */
export function floraTime(): number {
  return WIND.value;
}

/** Per-mesh fade config; values in flora-clock seconds / metres from the eye. */
export interface FloraFade {
  /** Flora-clock time this mesh was born: dissolves IN over ~0.8 s from here. */
  birth?: number;
  /** > 0: dissolve OUT beyond this eye distance (small ground cover). */
  fadeFar?: number;
  /** > 0: dissolve IN beyond this eye distance (far-LOD band crossfade). */
  fadeNear?: number;
  /**
   * Height above the root in metres, read by `floraHeight()`. Ground cover's
   * bend and walker interaction use this profile. Authored tree geometry is
   * identified by its branch attributes and uses its own restrained pivots.
   */
  height?: number;
  /**
   * RIGID props — boulders, stone slabs, anything that is not a plant.
   *
   * ⚠⚠ THE `sway` ATTRIBUTE IS NOT ENOUGH. It gates only the per-vertex rustle;
   * the whole-prop LAY-OVER, the gust RINGING and the walker's PART-THE-GRASS
   * push are all driven by the height profile alone, so a rock painted sway=0
   * still leaned, quivered at 5-8 Hz, and got shoved aside as the player walked
   * past it. That was the user report: "rocks jiggle in the wind like trees."
   * Set this and the entire wind block is skipped — one uniform branch, so it
   * costs nothing and saves the work on the most numerous scatter there is.
   */
  rigid?: boolean;
  /** Distant silhouettes need only the mean breeze, not the gust-cell field. */
  simpleWind?: boolean;
}

interface FadeUniforms {
  uSimpleWind: { value: number };
  uBirth: { value: number };
  uFadeFar: { value: number };
  uFadeNear: { value: number };
  uPlantH: { value: number };
  /** 1 = a plant (wind applies), 0 = rigid stone. See {@link FloraFade.rigid}. */
  uWindy: { value: number };
}

/** Build the shared fade/wind uniform block for one mesh. */
function fadeUniforms(opts?: FloraFade): FadeUniforms {
  return {
    uSimpleWind: { value: opts?.simpleWind ? 1 : 0 },
    uBirth: { value: opts?.birth ?? -100 },
    uFadeFar: { value: opts?.fadeFar ?? 0 },
    uFadeNear: { value: opts?.fadeNear ?? 0 },
    uPlantH: { value: opts?.height ?? 1 },
    uWindy: { value: opts?.rigid ? 0 : 1 },
  };
}

/** A plant's height, for the wind bend profile. Cached on the geometry — this
 *  is called once per scatter mesh and the bounding box is wanted anyway. */
export function floraHeight(geo: THREE.BufferGeometry): number {
  if (!geo.boundingBox) geo.computeBoundingBox();
  return Math.max(0.05, geo.boundingBox!.max.y);
}

/** The wind + fade vertex/fragment injection shared by the color and DEPTH
 *  materials — the shadow pass must sway and dither exactly like the beauty
 *  pass or shadows pop/stand still while the tree fades/moves. */
function injectFloraShader(shader: { uniforms: Record<string, unknown>; vertexShader: string; fragmentShader: string }, u: FadeUniforms): void {
  shader.uniforms.uSimpleWind = u.uSimpleWind;
  shader.uniforms.uWind = WIND;
  shader.uniforms.uBirth = u.uBirth;
  shader.uniforms.uFadeFar = u.uFadeFar;
  shader.uniforms.uFadeNear = u.uFadeNear;
  shader.uniforms.uFadeScale = FADE_SCALE;
  shader.uniforms.uFloraEye = EYE;
  shader.uniforms.uPlantH = u.uPlantH;
  shader.uniforms.uWindy = u.uWindy;
  Object.assign(shader.uniforms, WIND_UNIFORMS);
  shader.vertexShader = shader.vertexShader
    .replace("#include <common>", "varying float vCanopyFill;\n#include <common>")
    .replace("#include <begin_vertex>", "#include <begin_vertex>\nvCanopyFill = mix(1.0, 0.56 + 0.44 * smoothstep(-0.15, 0.85, normal.y), clamp(sway, 0.0, 1.0));")
    .replace(
      "#include <common>",
      "uniform float uSimpleWind;\nuniform float uWind;\nuniform float uBirth;\nuniform float uFadeFar;\nuniform float uFadeNear;\nuniform float uFadeScale;\nuniform vec3 uFloraEye;\nuniform float uPlantH;\nuniform float uWindy;\nattribute float sway;\nvarying float vFloraFade;\n"
        + WIND_GLSL + TREE_WIND_GLSL + "\n#include <common>",
    )
    .replace(
      "#include <begin_vertex>",
      [
        "#include <begin_vertex>",
        // RIGID props opt out of the whole block — bend, ring, rustle and the
        // walker push alike. `uWindy` is a uniform, so the branch is coherent
        // across the draw and the wind work is simply not done for stone.
        "if (uWindy > 0.0) {",
        "  // The plant's ROOT in planet-local space. Scatter meshes are parented to",
        "  // the planet, so an instance's translation column already IS that — which",
        "  // is the frame the wind field wants (nailed to the ground, and near",
        "  // planet-radius scale rather than the render frame's floating-origin one).",
        "  #ifdef USE_INSTANCING",
        "  mat4 hlM = instanceMatrix;",
        "  #else",
        "  mat4 hlM = modelMatrix;",
        "  #endif",
        "  vec3 root = vec3(hlM[3]);",
        "  vec3 rootWorld = root;",
        "  #ifdef USE_INSTANCING",
        "  rootWorld = (modelMatrix * vec4(root, 1.0)).xyz;",
        "  #endif",
        "  float windDetail = 1.0 - smoothstep(100.0, 300.0, distance(rootWorld, uFloraEye) / uFadeScale);",
        "  vec4 W = vec4(uWindFwd * uWindSpeed, 0.0);",
        "  if (uSimpleWind < 0.5 && windDetail > 0.0) W = mix(W, hlWindAt(root), windDetail);",
        "  float gust = W.w;",
        "  // The field is a tangent vector in PLANET-LOCAL space, but the vertex being",
        "  // moved is in the PLANT's own space — which carries a random yaw and an",
        "  // up-alignment. Taking the basis from the placement matrix's own columns is",
        "  // the whole conversion; deriving it from the planet up instead ignores the",
        "  // yaw, and every blade then leans in its own private direction.",
        "  vec3 axX = normalize(vec3(hlM[0]) + vec3(1e-9, 0.0, 0.0));",
        "  vec3 wUp = normalize(vec3(hlM[1]) + vec3(0.0, 1e-9, 0.0));",
        "  vec3 axZ = normalize(vec3(hlM[2]) + vec3(0.0, 0.0, 1e-9));",
        "  vec3 wFlat = W.xyz - wUp * dot(W.xyz, wUp);",
        "  float sp = length(wFlat);",
        "  vec3 wDir = sp > 1e-4 ? wFlat / sp : vec3(0.0);",
        "  vec2 bendDir = vec2(dot(wDir, axX), dot(wDir, axZ));",
        "  float ph = root.x * 0.21 + root.y * 0.17 + root.z * 0.13;",
        "  float hN = clamp(transformed.y / uPlantH, 0.0, 1.0);",
        "  if (abs(branchWind.w) > 0.0) {",
        "    float phase = uWindTime * 0.58 - dot(root - uWindAnchor, uWindFwd) * 0.035;",
        "    float branchDetail = uSimpleWind < 0.5 ? windDetail : 0.0;",
        "    transformed = hlTreeMotion(transformed, bendDir, sp, branchDetail, phase);",
        "  } else {",
        "",
        "  // Lay-over: a fraction of the plant's OWN height, squared along it so the",
        "  // root stays planted and the tip travels. Divided by a stiffness that grows",
        "  // with height, so grass folds over and a tree of the same code only leans.",
        "  float bend = hN * hN * uPlantH * (sp * 0.055 * uWindGain) / (1.0 + uPlantH * 0.8);",
        // A slow, spatially coherent crown motion under the faster leaf rustle.
        "  bend *= 1.0 + 0.24 * sin(uWindTime * 0.85 + dot(root - uWindAnchor, uWindFwd) * 0.035) * windDetail;",
        "  transformed.xz += bendDir * bend;",
        "  // Foreshorten: a bent stem does not also get taller.",
        "  transformed.y -= hN * bend * bend * 0.35 / max(uPlantH, 0.05);",
        "",
        "  // Ringing — a front passes and leaves the plant quivering at its own rate.",
        "  // This is the half that reads as a GUST rather than as a steady breeze.",
        "  float ring = sin(uWindTime * (5.0 + fract(ph) * 3.0) + ph) * gust * hN * hN * uPlantH * 0.05 * uWindGain;",
        "  transformed.xz += bendDir * ring / (1.0 + uPlantH * 0.8);",
        "",
        "  // Per-vertex rustle, gated by the `sway` attribute painted at build time",
        "  // (1 = foliage, 0 = bark/rock). Amplitude rides the live gust.",
        "  if (uSimpleWind < 0.5 && windDetail > 0.0) {",
        "  float vph = ph + transformed.x * 0.8 + transformed.z * 0.9 + transformed.y * 0.5;",
        "  float leafAmp = sway * (0.012 + 0.030 * sp + 0.055 * gust) * uWindGain * windDetail * smoothstep(0.05, 0.8, hN);",
        "  transformed.x += sin(uWindTime * 2.9 + vph) * leafAmp;",
        "  transformed.z += cos(uWindTime * 2.3 + vph * 1.31) * leafAmp;",
        "  transformed.y += sin(uWindTime * 1.9 + vph * 0.83) * leafAmp * 0.4;",
        "  }",
        "  }",
        "",
        "  // The walker parts the grass. Short plants only — a tree does not care,",
        "  // and pushing one aside as you brush past it looks absurd.",
        "  if (uWindPush > 0.0 && uPlantH < 2.5 && branchWind.w == 0.0) {",
        "    vec3 d = root - uWindPlayer;",
        "    vec3 dFlat = d - wUp * dot(d, wUp);",
        "    float pd = length(dFlat);",
        "    if (pd < uWindPushRadius * 1.2) {",
        "      float vert = 1.0 - smoothstep(1.2, 2.6, abs(dot(d, wUp)));",
        "      float push = smoothstep(uWindPushRadius, uWindPushRadius * 0.1, pd) * vert * uWindPush;",
        "      vec2 away = vec2(dot(dFlat, axX), dot(dFlat, axZ)) / max(pd, 0.02);",
        "      transformed.xz += away * push * hN * hN * uPlantH * 0.55;",
        "      transformed.y -= push * hN * hN * uPlantH * 0.18;",
        "    }",
        "  }",
        "}",
      ].join("\n"),
    )
    .replace(
      "#include <project_vertex>",
      [
        "#include <project_vertex>",
        "vFloraFade = clamp((uWind - uBirth) / 0.8, 0.0, 1.0);",
        "// Distance to the PLAYER eye in scene space — same value in the beauty",
        "// pass and in the sun/moon shadow pass, which render from different",
        "// cameras (see uFloraEye).",
        "vec4 floraWorld = vec4(transformed, 1.0);",
        "#ifdef USE_INSTANCING",
        "floraWorld = instanceMatrix * floraWorld;",
        "#endif",
        "floraWorld = modelMatrix * floraWorld;",
        "float floraD = distance(floraWorld.xyz, uFloraEye);",
        "float fadeFarD = uFadeFar * uFadeScale;",
        "if (uFadeFar > 0.0) vFloraFade *= 1.0 - smoothstep(fadeFarD - 40.0 * uFadeScale, fadeFarD, floraD);",
        "float fadeNearD = uFadeNear * uFadeScale;",
        "if (uFadeNear > 0.0) vFloraFade *= smoothstep(fadeNearD - 150.0 * uFadeScale, fadeNearD, floraD);",
      ].join("\n"),
    );
  shader.fragmentShader = shader.fragmentShader
    // Approximate sky occlusion inside foliage. Keep reflected light under
    // boughs; this scales only available indirect light, with no emissive floor.
    // Direct light, shadow tests, bark and rigid stone remain intact.
    // Depth shaders have no lighting chunk and therefore only inherit the fade.
    .replace("#include <lights_fragment_end>", "#include <lights_fragment_end>\nreflectedLight.indirectDiffuse *= vCanopyFill;")
    .replace("#include <common>", "varying float vFloraFade; varying float vCanopyFill;\n#include <common>")
    .replace(
      "#include <clipping_planes_fragment>",
      [
        "#include <clipping_planes_fragment>",
        "// Screen-door dissolve: cheap, opaque-pipeline-safe fade in/out.",
        "// >= not >: at fade 0 the pixels whose hash lands exactly on 0.0 must go",
        "// too, or a fully faded-out mesh leaks a few stray fragments.",
        "if (vFloraFade < 0.999 && fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) >= vFloraFade) discard;",
      ].join("\n"),
    );
}

/**
 * Flat-shaded instanced flora material: vertex colors carry bark/leaf,
 * instanceColor carries per-tree tint jitter, and a vertex-shader wind moves
 * the plants. Trees use a restrained stem rotation and nested branch pivots:
 * wood, foliage and snow move together. Ground cover retains its softer bend
 * and rustle response to the same shared weather field.
 *
 * FADE: `birth` dissolves the mesh IN over ~0.8 s via screen-door dithering
 * (LOD tiles materialize instead of popping); `fadeFar` dissolves small
 * ground cover OUT with distance; `fadeNear` dissolves the far-LOD band IN
 * with distance (the two bands crossfade). Every material instance shares one
 * compiled program — only uniform values differ per mesh.
 */
export function makeFloraMaterial(opts?: FloraFade): THREE.MeshLambertMaterial {
  const mat = new THREE.MeshLambertMaterial({ vertexColors: true });
  treeAttributeDefaults(mat);
  const u = fadeUniforms(opts);
  mat.onBeforeCompile = (shader) => injectFloraShader(shader, u);
  // Stylized: rim light pops foliage silhouettes; leaves stay matte (no sheen).
  // Ink by stature: a tree's trunk and canopy hold a drawn line; grass and
  // ground cover are per-blade depth chaos and can only strobe under one.
  addStylizedShading(mat, { rim: 0.34, rimPow: 2.8, spec: 0.0, weatherGround: true, ink: floraInk(opts) });
  return mat;
}

/** 1 for tree-stature plants, 0 for grass/ground cover (see makeFloraMaterial). */
function floraInk(opts?: FloraFade): number {
  return (opts?.height ?? 99) >= 2 ? 1 : 0;
}

/** Missing attributes mean ground cover/rigid props. Explicit defaults avoid
 * leaking the preceding tree draw's generic attribute state into those draws. */
function treeAttributeDefaults(material: THREE.Material): void {
  Object.assign(material, { defaultAttributeValues: {
    color: [1, 1, 1], uv: [0, 0], uv1: [0, 0], branchWind: [0, 0, 0, 0], twigWind: [0, 0, 0, 0],
  } });
}

/**
 * Color + shadow-depth material pair sharing the SAME fade uniforms, so the
 * cast shadow sways with the wind and dissolves with the mesh instead of
 * popping in at full strength. Assign `depthMaterial` to
 * `mesh.customDepthMaterial`.
 */
export function makeFloraMaterials(opts?: FloraFade): { material: THREE.MeshLambertMaterial; depthMaterial: THREE.MeshDepthMaterial } {
  const u = fadeUniforms(opts);
  const material = new THREE.MeshLambertMaterial({ vertexColors: true });
  treeAttributeDefaults(material);
  material.onBeforeCompile = (shader) => injectFloraShader(shader, u);
  addStylizedShading(material, { rim: 0.34, rimPow: 2.8, spec: 0.0, weatherGround: true, ink: floraInk(opts) });
  const depthMaterial = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
  treeAttributeDefaults(depthMaterial);
  depthMaterial.onBeforeCompile = (shader) => injectFloraShader(shader, u);
  return { material, depthMaterial };
}
